import { BaseOption } from 'ui/selects';
import { ProjectMembership, Role, TeamMembership } from '../../types';

export const isProjectRole = (role: Role) => role.scope === 'Project';

export const isTeamRole = (role: Role) => role.scope === 'Team';

export const getProjectRoleByName = (roles: Role[], name: string) =>
  roles.find((role) => role.name === name && isProjectRole(role));

export const getTeamRoleByName = (roles: Role[], name: string) =>
  roles.find((role) => role.name === name && isTeamRole(role));

export const permissionsByName = (roles: Role[]) =>
  roles.reduce(
    (acc, role: Role) => ({
      ...acc,
      [role.name]: role.permissions,
    }),
    {} as Record<string, number[]>,
  );

export const combineProjectAndTeamRoles = (roles: Role[]) => {
  const projectRoles = roles.filter(isProjectRole);
  const teamRoles = roles.filter(isTeamRole);
  const teamRolePermissionsByName = permissionsByName(teamRoles);
  return projectRoles
    .filter((role: Role) => !!teamRolePermissionsByName[role.name])
    .map((role: Role) => {
      const equivalentTeamPermissions = teamRolePermissionsByName[role.name];
      return {
        ...role,
        permissions: [...role.permissions, ...equivalentTeamPermissions],
        scope: 'ProjectTeam',
      };
    });
};

/**
 * Creates dropdown options for BasicSelect when selecting a role
 */
export const createRoleOptions = (roles: Role[]): BaseOption[] => {
  const combinedRoles = combineProjectAndTeamRoles(roles);
  const roleOptions = combinedRoles
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((option) => ({
      id: option.id.toString(),
      label: option.name,
      value: option.name,
    }));

  return roleOptions;
};

/**
 * Updates the membership role value for a project or team
 */
export const updateMembershipRole = (
  memberships: ProjectMembership[] | TeamMembership[] = [],
  projectId: number,
  roleId?: number,
) => {
  if (!roleId) return memberships;

  return memberships.map((membership) => {
    if (membership.projectId === projectId) {
      return { ...membership, roleId };
    }
    return membership;
  });
};
